import { onChildAdded, onDisconnect, onValue, ref } from 'firebase/database';
import { db } from './app';
import { requireUid } from './auth';
import { paths } from './paths';
import { exists, newKey, pushValue, removeValue, updateValues, writeValue, SiroccoDataError } from './rtdb';
import { appelNatifDisponible, ConnexionNative } from './appelNatif';
import { estCompteOfficiel } from './officiel';
import { t } from '../i18n';

/**
 * Appeler quelqu'un.
 *
 * La voix ne passe pas par nous : les deux telephones se parlent
 * directement, en WebRTC. La base ne sert qu'a les presenter — chacun y
 * depose sa description et les chemins reseau qu'il propose, l'autre les
 * lit. C'est tout ce que la « signalisation » veut dire, et c'est
 * exactement ce que la Realtime Database sait faire de mieux : deux
 * ecoutes ouvertes, et ce que l'un ecrit arrive chez l'autre.
 *
 * Deux limites qu'il faut connaitre.
 *
 * On ne sonne que chez quelqu'un dont l'application est ouverte. Reveiller
 * un telephone verrouille demande une notification VoIP, donc le droit
 * `aps-environment`, que la signature actuelle n'a pas — le meme mur que
 * les notifications. Le jour ou il tombe, c'est ce module qu'on appellera
 * depuis la notification, sans rien y changer.
 *
 * Et il n'y a pas de relais. Les deux telephones cherchent un chemin
 * direct, aide d'un serveur STUN public qui leur dit seulement comment on
 * les voit de l'exterieur. Cela suffit dans la grande majorite des cas,
 * mais certains reseaux — un partage de connexion, un wifi d'entreprise —
 * n'en laissent aucun passer. Il faudrait alors un serveur TURN, qui
 * relaie le son et se paie au gigaoctet. L'appel echoue proprement plutot
 * que de rester muet : voir `onEchec`.
 */

/** Ce que devient un appel, du premier anneau au dernier. */
export type CallStatus = 'ringing' | 'accepted' | 'declined' | 'ended';

/** Un appel, tel qu'il est ecrit dans `calls/{callId}`. */
export interface CallRecord {
  callerId: string;
  calleeId: string;
  /** Appel video : les deux camera ouvertes des le depart. */
  video?: boolean;
  status: CallStatus;
  createdAt: number;
  offer?: { type: string; sdp: string };
  answer?: { type: string; sdp: string };
  /* La montee en video, en cours d'appel. Le drapeau previent les deux
     bouts, les deux descriptions renegocient la connexion existante. */
  wantVideo?: boolean;
  offer2?: { type: string; sdp: string };
  answer2?: { type: string; sdp: string };
}

/** Ce qui sonne : juste de quoi afficher qui appelle. */
export interface IncomingCall {
  callId: string;
  callerId: string;
  createdAt: number;
  /** Annonce a l'ecran qu'il faudra ouvrir la camera en decrochant. */
  video?: boolean;
}

/*
  Le serveur qui dit a un telephone comment on le voit depuis l'exterieur.
  Il ne voit passer aucun son — seulement une adresse. Celui de Google est
  public et gratuit ; c'est l'usage courant.
*/
const SERVEURS: RTCConfiguration = {
  iceServers: [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }],
};

/** Au-dela, on considere que personne ne decroche. */
export const SONNERIE_MAX_MS = 35_000;

/**
 * Ce qu'on demande a la camera, et a l'encodeur.
 *
 * On visait 720p, pour epargner l'encodeur et la bande passante. C'etait
 * un mauvais calcul : un ecran de telephone fait bien plus de pixels que
 * le flux n'en portait, l'image etait donc etiree et floue en
 * permanence. Capturer haut ne coute rien sur un mauvais reseau — WebRTC
 * redescend tout seul quand le debit ne suit pas — tandis que capturer
 * bas pose un plafond que rien ne peut lever.
 */
const IMAGE = { facingMode: 'user', width: { ideal: 1920 }, height: { ideal: 1080 } } as const;

/** Le plafond de l'encodeur video. Sans consigne, il reste bas. */
const PLAFOND_VIDEO = 3_000_000;

/**
 * Dit a l'encodeur ce qu'il a le droit de faire.
 *
 * A reposer une fois la connexion faite : les parametres d'un emetteur ne
 * survivent pas toujours a la negociation.
 */
async function reglerLeDebit(pc: RTCPeerConnection): Promise<void> {
  for (const envoyeur of pc.getSenders()) {
    if (envoyeur.track?.kind !== 'video') continue;
    const reglages = envoyeur.getParameters();
    // Un emetteur qui n'a pas encore negocie n'a pas d'encodage : le
    // creer a la main serait refuse, on repassera a la connexion.
    if (!reglages.encodings?.length) continue;
    for (const encodage of reglages.encodings) {
      encodage.maxBitrate = PLAFOND_VIDEO;
      encodage.maxFramerate = 30;
    }
    await envoyeur.setParameters(reglages).catch(() => undefined);
  }
}

/** Ce qu'un appel en cours offre a l'ecran qui l'affiche. */
export interface ActiveCall {
  callId: string;
  /** Vrai si les cameras sont de la partie. */
  avecVideo: boolean;
  /**
   * L'appel est porte par l'application et non par la vue web.
   *
   * L'ecran a besoin de le savoir : il n'y a alors ni piste ni balise
   * audio a surveiller, la bibliotheque native joue la voix elle-meme.
   */
  natif: boolean;
  /** Mon propre flux, pour la vignette de retour. */
  local: MediaStream;
  /** Coupe sa propre camera. Rend l'etat obtenu. */
  toggleCamera: () => boolean;
  /** Passe de la camera de l'ecran a celle du dos. Rend `true` pour celle de l'ecran. */
  retournerCamera: () => Promise<boolean>;
  /** Passe un appel vocal en visio, sans le raccrocher. */
  passerEnVideo: () => Promise<void>;
  /** L'autre bout, quel que soit le sens de l'appel. */
  otherId: string;
  role: 'caller' | 'callee';
  /** Le son de l'autre, a poser sur une balise audio. */
  remote: MediaStream;
  /** Coupe son propre micro. Rend l'etat obtenu. */
  toggleMute: () => boolean;
  /** Raccroche, previent l'autre et libere le micro. */
  hangUp: () => Promise<void>;
}

/** Ce que l'appel raconte a l'ecran pendant qu'il se joue. */
export interface CallHandlers {
  onStatus: (status: CallStatus) => void;
  /** Le lien est etabli, la voix passe. */
  onConnected: () => void;
  /** Perdu, refuse par le reseau : la phrase est deja en francais. */
  onEchec: (message: string) => void;
  /** L'appel est passe a la video — de mon fait ou de celui de l'autre. */
  onVideo?: () => void;
  /** Une piste de l'autre vient d'arriver : la balise doit etre relancee. */
  onFlux?: () => void;
  /** La camera de l'autre est ouverte, ou ne l'est pas. */
  onCameraEnFace?: (ouverte: boolean) => void;
}

/**
 * Tout ce qu'un appel laisse ouvert : la connexion, le micro, et les
 * ecoutes posees sur la base. Un appel qui se termine doit tout refermer,
 * sans quoi le micro reste allume — la pastille rouge de l'iPhone le dit
 * a l'utilisateur avant que nous le remarquions.
 */
interface Attaches {
  pc: RTCPeerConnection;
  local: MediaStream;
  arrets: Array<() => void>;
  /** La meme connexion, sous son vrai jour, quand elle est native. */
  natif: ConnexionNative | null;
}

function fermer(a: Attaches): void {
  for (const arret of a.arrets) arret();
  a.arrets.length = 0;
  for (const piste of a.local.getTracks()) piste.stop();
  a.pc.close();
}

/**
 * Le micro, et la camera si l'appel est video.
 *
 * Les deux sont demandes en une fois : deux demandes de suite feraient
 * deux fenetres de permission, et la seconde arriverait alors que l'autre
 * decroche deja.
 */
async function micro(avecVideo: boolean): Promise<MediaStream> {
  /*
    En natif, le micro s'ouvre de l'autre cote et n'a rien a faire ici.

    C'est meme tout l'enjeu : `getUserMedia` est ce qui donne la session
    audio a WebKit, et l'appeler « juste pour la forme » rendrait la
    correction sans objet. Le flux vide qu'on rend ne sert qu'a la
    vignette de retour, qui n'a rien a montrer pour un appel vocal.
  */
  if (appelNatifDisponible()) return new MediaStream();

  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: avecVideo ? IMAGE : false,
    });
  } catch {
    throw new Error(
      avecVideo ? t.appels.erreurs.camera : t.appels.erreurs.micro,
    );
  }
}

/**
 * Prepare la connexion : le micro dedans, le son de l'autre dehors, et
 * les chemins reseau deposes au fur et a mesure qu'ils se decouvrent.
 */
async function preparer(
  callId: string,
  role: 'caller' | 'callee',
  local: MediaStream,
  remote: MediaStream,
  handlers: CallHandlers,
  avecVideo: boolean,
): Promise<{ pc: RTCPeerConnection; natif: ConnexionNative | null }> {
  /*
    Le natif se fait passer pour une `RTCPeerConnection`.

    Il en implemente exactement ce que ce module utilise — offre, reponse,
    candidats, etat, fermeture — et rien d'autre. Le detour par une
    conversion assumee vaut mieux que la solution propre : decrire une
    interface commune obligerait a tordre les signatures du navigateur,
    tres precises, pour qu'elles couvrent les deux. Tout le reste du
    fichier — la signalisation, les ecoutes, l'enchainement — ne voit
    aucune difference, et c'est ce qui rend le changement sur.

  */
  if (appelNatifDisponible()) {
    const natif = new ConnexionNative();
    // Un appel vocal commence a l'ecouteur ; une visio au haut-parleur,
    // puisqu'on ne tient pas l'ecran contre son oreille.
    await natif.ouvrir(SERVEURS, avecVideo, avecVideo);
    const pc = natif as unknown as RTCPeerConnection;
    poserLesRappels(callId, role, remote, handlers, pc);
    return { pc, natif };
  }

  const pc = new RTCPeerConnection(SERVEURS);
  for (const piste of local.getTracks()) pc.addTrack(piste, local);
  poserLesRappels(callId, role, remote, handlers, pc);
  void reglerLeDebit(pc);
  return { pc, natif: null };
}

/** Ce que la connexion raconte, quel que soit le chemin qui la porte. */
function poserLesRappels(
  callId: string,
  role: 'caller' | 'callee',
  remote: MediaStream,
  handlers: CallHandlers,
  pc: RTCPeerConnection,
): void {

  /*
    Le son de l'autre arrive dans un flux cree ici plutot que dans celui
    que WebRTC propose : l'ecran a deja pose ce flux sur sa balise audio
    avant que la piste existe, et remplacer la source apres coup ne se
    remet pas toujours a jouer.

    Mais ce flux est vide au moment ou on le pose, et WebKit tient alors
    la balise pour vide : il ne repart pas tout seul quand une piste s'y
    ajoute. D'ou l'avis a l'ecran a chaque arrivee — c'est exactement ce
    qui rendait les appels muets.
  */
  pc.ontrack = (event) => {
    for (const piste of event.streams[0]?.getTracks() ?? []) remote.addTrack(piste);
    handlers.onFlux?.();
  };

  pc.onicecandidate = (event) => {
    if (!event.candidate) return;
    void pushValue(paths.callCandidates(callId, role), event.candidate.toJSON());
  };

  pc.onconnectionstatechange = () => {
    if (pc.connectionState === 'connected') {
      void reglerLeDebit(pc);
      handlers.onConnected();
    }
    if (pc.connectionState === 'failed') {
      handlers.onEchec(t.appels.erreurs.connexion);
    }
  };
}

/**
 * L'appel se termine aussi quand on part sans le dire.
 *
 * Raccrocher ecrit `status: 'ended'`, et l'autre bout le lit. Mais si
 * l'application est tuee, si le telephone perd le reseau ou si la batterie
 * lache, personne n'ecrit rien : l'autre restait devant un appel qui ne
 * finissait jamais, micro ouvert.
 *
 * La consigne est donc deposee a l'avance, chez Firebase : « si je me
 * deconnecte, ecris ceci ». C'est le serveur qui l'execute, sans nous, et
 * elle se retire d'elle-meme a la fermeture propre puisque le noeud entier
 * disparait alors.
 */
function couperSiJePars(callId: string): () => void {
  const noeud = ref(db(), paths.callField(callId, 'status'));
  void onDisconnect(noeud).set('ended').catch(() => undefined);
  return () => void onDisconnect(noeud).cancel().catch(() => undefined);
}

/**
 * Ce que ma camera montre, dit a l'autre bout.
 *
 * Le flux ne le dit pas. Une piste coupee continue d'arriver, en noir ;
 * une camera jamais ouverte n'arrive pas du tout. Dans les deux cas
 * l'autre a un rectangle vide sous les yeux, sans savoir si c'est un
 * choix, une panne ou une attente. La base porte donc le drapeau, comme
 * elle porte le reste de la presentation, et l'ecran met un visage a la
 * place du noir.
 */
function direMaCamera(callId: string, role: 'caller' | 'callee', ouverte: boolean): void {
  void writeValue(paths.callCamera(callId, role), ouverte).catch(() => undefined);
}

/** Ecoute les chemins que l'autre propose, et les essaie. */
function suivreCandidats(callId: string, deQui: 'caller' | 'callee', pc: RTCPeerConnection) {
  return onChildAdded(ref(db(), paths.callCandidates(callId, deQui)), (snap) => {
    const candidat = snap.val() as RTCIceCandidateInit | null;
    if (!candidat) return;
    // Un candidat qui arrive trop tot ou en double ne doit pas tout casser.
    void pc.addIceCandidate(new RTCIceCandidate(candidat)).catch(() => undefined);
  });
}

/**
 * Passer un appel vocal en visio, sans le refaire.
 *
 * La connexion existe deja et transporte la voix : on ne la rouvre pas, on
 * la renegocie. Chacun ouvre sa camera, ajoute sa piste, et une seconde
 * description s'echange — le meme mecanisme qu'a l'etablissement, en
 * deuxieme passage. La voix ne s'interrompt pas pendant ce temps.
 *
 * Celui qui propose n'est pas celui qui a appuye : c'est toujours
 * l'appelant. Deux offres qui se croisent, WebRTC les refuse toutes les
 * deux ; un offreur decide d'avance supprime le probleme sans arbitrage.
 *
 * Rend la fonction qui declenche la montee. Les ecoutes, elles, sont
 * posees tout de suite : l'autre bout peut appuyer le premier.
 */
function monterEnVideo(
  callId: string,
  role: 'caller' | 'callee',
  attaches: Attaches,
  handlers: CallHandlers,
  dejaVideo: boolean,
): () => Promise<void> {
  const { pc, local } = attaches;
  let maCamera = dejaVideo;
  /*
    La derniere offre que j'ai deposee.

    Firebase renvoie a chacun ce qu'il ecrit : sans reperer la mienne, je
    repondrais a ma propre offre. Un simple drapeau ne suffit pas — il
    restait leve pour toujours, si bien que le premier a montrer sa camera
    empechait l'autre de montrer la sienne ensuite. C'est la description
    elle-meme qui distingue, et elle change a chaque tour.
  */
  let maDerniereOffre: string | null = null;

  /**
   * Ouvre ma camera, et elle seule.
   *
   * Montrer sa camera n'allume pas celle d'en face. C'etait le cas —
   * les deux bouts ouvraient la leur sur le meme drapeau — et cela
   * revenait a decider a la place de l'autre qu'il se montrait. Il voit
   * mon image, et allume la sienne s'il le veut.
   */
  async function ouvrirMaCamera(): Promise<boolean> {
    if (maCamera) return true;
    maCamera = true;
    try {
      /*
        La camera s'ouvre du cote qui porte l'appel. En natif, surtout pas
        ici : `getUserMedia` rendrait la session audio a WebKit, et le
        haut-parleur redeviendrait incommandable.
      */
      if (attaches.natif) {
        await attaches.natif.demarrerVideo();
      } else {
        const flux = await navigator.mediaDevices.getUserMedia({ video: IMAGE });
        for (const piste of flux.getVideoTracks()) {
          local.addTrack(piste);
          pc.addTrack(piste, local);
        }
        void reglerLeDebit(pc);
      }
    } catch {
      maCamera = false;
      handlers.onEchec(t.appels.erreurs.camera);
      return false;
    }
    direMaCamera(callId, role, true);
    handlers.onVideo?.();
    return true;
  }

  /** Repondre a l'offre de l'autre, sans rien ouvrir de mon cote. */
  async function repondre(offre: RTCSessionDescriptionInit): Promise<void> {
    if (pc.signalingState !== 'stable') return;
    await pc.setRemoteDescription(new RTCSessionDescription(offre));
    const reponse = await pc.createAnswer();
    await pc.setLocalDescription(reponse);
    await writeValue(paths.callField(callId, 'answer2'), {
      type: reponse.type,
      sdp: reponse.sdp ?? '',
    });
  }

  /*
    Celui qui propose est celui qui a appuye, chacun a son tour.

    Vocal et visio ne sont pas deux appels differents : c'est le meme, et
    chacun y montre sa camera quand il veut, dans l'ordre qu'il veut. Un
    appel video n'est que celui qui commence avec les deux ouvertes.

    L'offreur ne peut donc pas etre fixe d'avance : une reponse ne sait
    pas ajouter de piste, si bien que celui qui allume sa camera doit
    offrir lui-meme, sinon sa piste n'entre jamais dans la connexion.

    Reste le croisement, si les deux appuient dans la meme seconde. Une
    connexion qui a deja une offre en attente ne peut pas en accepter une
    autre : l'appele cede — il oublie la sienne et repond a celle de
    l'appelant — puis renvoie la sienne dans la foulee. Sa camera arrive
    un tour plus tard, ce qui vaut mieux qu'un appel bloque.
  */
  attaches.arrets.push(
    onValue(ref(db(), paths.callField(callId, 'offer2')), (snap) => {
      const offre = snap.val() as RTCSessionDescriptionInit | null;
      if (!offre || offre.sdp === maDerniereOffre) return;

      // L'autre montre sa camera : l'ecran passe en video sans ouvrir la
      // mienne.
      handlers.onVideo?.();

      if (pc.signalingState === 'stable') {
        void repondre(offre).catch(() => undefined);
        return;
      }
      if (role !== 'callee') return;
      maDerniereOffre = null;
      void repondre(offre)
        .then(() => (maCamera ? proposer() : undefined))
        .catch(() => undefined);
    }),
    onValue(ref(db(), paths.callField(callId, 'answer2')), (snap) => {
      const reponse = snap.val() as RTCSessionDescriptionInit | null;
      if (!reponse || pc.signalingState !== 'have-local-offer') return;
      void pc.setRemoteDescription(new RTCSessionDescription(reponse)).catch(() => undefined);
    }),
  );

  /** Depose une nouvelle description : ma camera vient de s'ajouter. */
  async function proposer(): Promise<void> {
    const offre = await pc.createOffer();
    await pc.setLocalDescription(offre);
    maDerniereOffre = offre.sdp ?? '';
    await writeValue(paths.callField(callId, 'offer2'), {
      type: offre.type,
      sdp: offre.sdp ?? '',
    });
  }

  /*
    Montrer sa camera avant que l'autre ait decroche.

    L'ecran d'appel offre ses commandes des qu'on appuie sur « appeler »,
    et non plus seulement une fois la voix passee : on veut pouvoir se
    mettre en visio, sur le haut-parleur ou en silencieux pendant que ca
    sonne. Mais une connexion qui attend encore sa reponse est en
    `have-local-offer` : y poser une seconde description locale echoue.

    La camera s'ouvre donc tout de suite — c'est ce qu'on a demande — et
    la proposition attend que la connexion redevienne stable, c'est-a-dire
    l'instant ou l'autre decroche. On regarde toutes les deux cent
    cinquante millisecondes, et seulement tant qu'une proposition attend :
    au repos cela ne coute rien.
  */
  let enAttente = false;
  let guet: ReturnType<typeof setInterval> | null = null;

  function proposerDesQueStable(): void {
    if (guet) return;
    guet = setInterval(() => {
      if (!enAttente || pc.signalingState !== 'stable') return;
      enAttente = false;
      if (guet) clearInterval(guet);
      guet = null;
      void proposer().catch(() => undefined);
    }, 250);
  }

  attaches.arrets.push(() => {
    if (guet) clearInterval(guet);
    guet = null;
  });

  return async () => {
    if (!(await ouvrirMaCamera())) return;
    if (pc.signalingState !== 'stable') {
      enAttente = true;
      proposerDesQueStable();
      return;
    }
    await proposer();
  };
}

/** Suit la vie de l'appel, et referme des qu'il s'arrete. */
function suivreStatut(
  callId: string,
  attaches: Attaches,
  handlers: CallHandlers,
  surFin: () => void,
) {
  return onValue(ref(db(), paths.callField(callId, 'status')), (snap) => {
    const status = (snap.val() as CallStatus | null) ?? 'ended';
    handlers.onStatus(status);
    if (status === 'declined' || status === 'ended') {
      fermer(attaches);
      surFin();
    }
  });
}

/**
 * Appelle un membre.
 *
 * L'ordre compte : l'appel existe en base avant d'etre annonce chez
 * l'autre. Les regles de `incomingCalls` verifient l'expediteur, et une
 * annonce qui arriverait la premiere pointerait vers un appel qui n'existe
 * pas encore.
 *
 * **On n'appelle que ses amis.** Un message venu d'un inconnu se lit quand
 * on veut ; un telephone qui sonne s'impose, et se refuse a peine plus
 * vite qu'il n'a derange. Les regles le refusent aussi — c'est la, et non
 * ici, que la porte est reellement fermee : cette lecture ne sert qu'a le
 * dire en francais plutot qu'en `PERMISSION_DENIED`, et a ne pas ouvrir
 * micro et camera pour rien.
 */
export async function placeCall(
  calleeId: string,
  handlers: CallHandlers,
  avecVideo = false,
): Promise<ActiveCall> {
  const uid = requireUid();

  // Le compte de l'application ne recoit pas d'appel : voir `officiel.ts`.
  if (await estCompteOfficiel(calleeId)) {
    throw new SiroccoDataError(
      t.appels.erreurs.compteOfficiel,
      'compte-officiel',
    );
  }

  if (!(await exists(paths.friend(uid, calleeId)))) {
    throw new SiroccoDataError(
      t.appels.erreurs.pasAmi,
      'pas-ami',
    );
  }

  const callId = newKey(paths.calls());
  const local = await micro(avecVideo);
  const remote = new MediaStream();

  const { pc, natif } = await preparer(callId, 'caller', local, remote, handlers, avecVideo);
  const attaches: Attaches = { pc, local, arrets: [], natif };

  try {
    const offre = await pc.createOffer();
    await pc.setLocalDescription(offre);

    await writeValue(paths.call(callId), {
      callerId: uid,
      calleeId,
      video: avecVideo,
      status: 'ringing',
      createdAt: Date.now(),
      offer: { type: offre.type, sdp: offre.sdp ?? '' },
    } satisfies CallRecord);

    // Le type d'appel voyage avec l'annonce : l'ecran qui sonne l'affiche
    // avant d'avoir le droit de lire `calls/{callId}`, reserve aux deux
    // parties une fois l'appel accepte.
    await writeValue(paths.incomingCall(calleeId, callId), {
      callerId: uid,
      createdAt: Date.now(),
      video: avecVideo,
    });
  } catch (cause) {
    fermer(attaches);
    throw cause;
  }

  // La reponse de l'autre, quand il decroche.
  attaches.arrets.push(
    onValue(ref(db(), paths.callField(callId, 'answer')), (snap) => {
      const reponse = snap.val() as { type: string; sdp: string } | null;
      if (!reponse || pc.currentRemoteDescription) return;
      void pc
        .setRemoteDescription(new RTCSessionDescription(reponse as RTCSessionDescriptionInit))
        .catch(() => handlers.onEchec(t.appels.erreurs.pasAbouti));
    }),
  );
  attaches.arrets.push(couperSiJePars(callId));
  attaches.arrets.push(suivreCandidats(callId, 'callee', pc));
  attaches.arrets.push(
    suivreStatut(callId, attaches, handlers, () => void nettoyer(callId, calleeId)),
  );

  return brancher(callId, calleeId, 'caller', remote, attaches, uid, avecVideo, handlers);
}

/**
 * Decroche. L'offre est deja en base : on y repond, et l'appel bascule.
 */
export async function answerCall(callId: string, handlers: CallHandlers): Promise<ActiveCall> {
  const uid = requireUid();

  // Ce que l'appelant a propose decide de ce qu'on ouvre chez soi.
  const appel = await lireAppel(callId);
  if (!appel?.offer) throw new Error(t.appels.erreurs.nExistePlus);
  const avecVideo = Boolean(appel.video);

  const local = await micro(avecVideo);
  const remote = new MediaStream();

  const { pc, natif } = await preparer(callId, 'callee', local, remote, handlers, avecVideo);
  const attaches: Attaches = { pc, local, arrets: [], natif };

  try {
    await pc.setRemoteDescription(
      new RTCSessionDescription(appel.offer as RTCSessionDescriptionInit),
    );
    const reponse = await pc.createAnswer();
    await pc.setLocalDescription(reponse);

    await updateValues({
      [paths.callField(callId, 'answer')]: {
        type: reponse.type,
        sdp: reponse.sdp ?? '',
      },
      [paths.callField(callId, 'status')]: 'accepted',
    });
    await removeValue(paths.incomingCall(uid, callId));

    attaches.arrets.push(couperSiJePars(callId));
    attaches.arrets.push(suivreCandidats(callId, 'caller', pc));
    attaches.arrets.push(
      suivreStatut(callId, attaches, handlers, () => void nettoyer(callId, uid)),
    );

    return brancher(callId, appel.callerId, 'callee', remote, attaches, uid, avecVideo, handlers);
  } catch (cause) {
    fermer(attaches);
    throw cause;
  }
}

/** Refuse sans decrocher : l'autre l'apprend tout de suite. */
export async function declineCall(callId: string): Promise<void> {
  const uid = requireUid();
  await writeValue(paths.callField(callId, 'status'), 'declined');
  await removeValue(paths.incomingCall(uid, callId));
}

/**
 * Ce qui sonne chez moi.
 *
 * Une ecoute ouverte tant que l'application l'est : c'est elle, et rien
 * d'autre, qui fait apparaitre l'appel entrant.
 */
export function watchIncomingCalls(onCall: (calls: IncomingCall[]) => void): () => void {
  const uid = requireUid();
  return onValue(ref(db(), paths.incomingCalls(uid)), (snap) => {
    const brut = (snap.val() as Record<string, Omit<IncomingCall, 'callId'>> | null) ?? {};
    const frais = Object.entries(brut)
      // Un appel qu'on n'a pas vu passer a expire : il ne doit pas sonner
      // au prochain lancement de l'application.
      .filter(([, appel]) => Date.now() - appel.createdAt < SONNERIE_MAX_MS)
      .map(([callId, appel]) => ({ callId, ...appel }));
    onCall(frais);
  });
}

/* --- Rouages ------------------------------------------------------- */

async function lireAppel(callId: string): Promise<CallRecord | null> {
  const { readValue } = await import('./rtdb');
  return readValue<CallRecord>(paths.call(callId));
}

/**
 * Efface la trace d'un appel termine : elle n'a plus rien a dire.
 *
 * L'ordre n'est pas indifferent, et il a coute cher.
 *
 * La regle qui autorise a effacer `callCandidates/{callId}` demande a
 * `root` si l'on est bien l'un des deux bouts de `calls/{callId}` — et
 * `root`, dans les regles, voit l'etat d'avant l'ecriture. Les trois
 * suppressions partaient ensemble : celle de l'appel arrivait la
 * premiere, la regle des candidats perdait alors son point d'appui, et
 * le refus etait avale par le `.catch`. Sur la base reelle, soixante et
 * onze jeux de candidats sur soixante et onze avaient survecu a leur
 * appel — cent trente-six kilo-octets, plus de la moitie de la base,
 * amasses en six jours.
 *
 * Les candidats partent donc d'abord, l'appel ensuite.
 */
async function nettoyer(callId: string, calleeId: string): Promise<void> {
  await removeValue(`callCandidates/${callId}`).catch(() => undefined);
  await Promise.all([
    removeValue(paths.call(callId)).catch(() => undefined),
    removeValue(paths.incomingCall(calleeId, callId)).catch(() => undefined),
  ]);
}

/** Assemble ce que l'ecran manipule : couper le micro, raccrocher. */
function brancher(
  callId: string,
  otherId: string,
  role: 'caller' | 'callee',
  remote: MediaStream,
  attaches: Attaches,
  uid: string,
  avecVideo: boolean,
  handlers: CallHandlers,
): ActiveCall {
  /** Micro et camera coupes, tenus ici : le natif n'a pas de piste a lire. */
  let coupeNatif = false;
  let camNatifCoupee = false;
  /** La camera filmee, pour le chemin de la vue web. */
  let avant = true;
  /* Deux doubles appuis trop proches ne se marchent pas dessus. */
  let retournement: Promise<boolean> | null = null;

  // Un appel qui part en visio a deja sa camera ouverte : il le dit tout
  // de suite, avant meme que l'autre decroche.
  if (avecVideo) direMaCamera(callId, role, true);

  const enFace = role === 'caller' ? 'callee' : 'caller';
  attaches.arrets.push(
    onValue(ref(db(), paths.callCamera(callId, enFace)), (snap) => {
      handlers.onCameraEnFace?.(snap.val() === true);
    }),
  );

  return {
    callId,
    otherId,
    role,
    remote,
    avecVideo,
    natif: attaches.natif !== null,
    local: attaches.local,
    passerEnVideo: monterEnVideo(callId, role, attaches, handlers, avecVideo),
    toggleCamera: () => {
      // Meme raison que pour le micro : la piste native n'est pas ici.
      if (attaches.natif) {
        camNatifCoupee = !camNatifCoupee;
        void attaches.natif.couperCamera(camNatifCoupee).catch(() => undefined);
        direMaCamera(callId, role, !camNatifCoupee);
        return camNatifCoupee;
      }
      const piste = attaches.local.getVideoTracks()[0];
      if (!piste) return false;
      piste.enabled = !piste.enabled;
      direMaCamera(callId, role, piste.enabled);
      return !piste.enabled;
    },
    retournerCamera: () => {
      retournement ??= (async () => {
        if (attaches.natif) return attaches.natif.retournerCamera();

        const ancienne = attaches.local.getVideoTracks()[0];
        if (!ancienne) return avant;
        const envoyeur = attaches.pc.getSenders().find((e) => e.track === ancienne);
        const voulue = !avant;

        /*
          La nouvelle piste remplace l'ancienne dans l'emetteur, sans
          renegocier : l'autre voit l'image changer, rien d'autre. Les
          contraintes sont celles de l'ouverture, seul le cote change.

          L'ancienne s'arrete d'abord. Beaucoup de telephones Android
          refusent d'ouvrir une camera tant que l'autre tourne.
        */
        const ouvrir = (ecran: boolean) =>
          navigator.mediaDevices.getUserMedia({
            video: { ...IMAGE, facingMode: { exact: ecran ? 'user' : 'environment' } },
          });
        ancienne.stop();
        let flux: MediaStream;
        let obtenue = voulue;
        try {
          flux = await ouvrir(voulue);
        } catch {
          // Pas d'autre camera : on rouvre celle qu'on avait.
          flux = await ouvrir(avant);
          obtenue = avant;
        }
        const neuve = flux.getVideoTracks()[0];
        neuve.enabled = ancienne.enabled;
        await envoyeur?.replaceTrack(neuve);
        attaches.local.removeTrack(ancienne);
        attaches.local.addTrack(neuve);
        avant = obtenue;
        return avant;
      })().finally(() => {
        retournement = null;
      });
      return retournement;
    },
    toggleMute: () => {
      /*
        En natif, la piste n'est pas ici : c'est la bibliotheque qui la
        tient. L'ecran veut une reponse tout de suite — il bascule un
        bouton — donc on lui rend l'etat voulu et le natif le suit.
      */
      if (attaches.natif) {
        coupeNatif = !coupeNatif;
        void attaches.natif.muter(coupeNatif).catch(() => undefined);
        return coupeNatif;
      }
      const piste = attaches.local.getAudioTracks()[0];
      if (!piste) return false;
      piste.enabled = !piste.enabled;
      return !piste.enabled;
    },
    hangUp: async () => {
      fermer(attaches);
      // Le statut previent l'autre bout ; le menage suit.
      await writeValue(paths.callField(callId, 'status'), 'ended').catch(() => undefined);
      await nettoyer(callId, role === 'caller' ? otherId : uid);
    },
  };
}
